import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminGuard from '../../components/admin/AdminGuard';
import {
  fetchFeaturedAdmin,
  fetchAllProjectsAdmin,
  addToFeatured,
  removeFromFeatured,
  updateFeatured,
  batchUpdateFeaturedOrder,
} from '../../services/adminService';

export default function AdminFeaturedPage() {
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [all,      setAll]      = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(null); // item.id being saved
  const [err,      setErr]      = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const [feat, projects] = await Promise.all([fetchFeaturedAdmin(), fetchAllProjectsAdmin()]);
      setFeatured(feat);
      setAll(projects);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const featuredIds = new Set(featured.map((f) => f.project_id));
  const available   = all.filter((p) => !featuredIds.has(p.id));

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleAdd = async (projectId) => {
    try { await addToFeatured(projectId); load(); }
    catch (e) { setErr(e.message); }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('홈 피처드에서 제거할까요?')) return;
    try { await removeFromFeatured(id); load(); }
    catch (e) { setErr(e.message); }
  };

  const handleToggle = async (item) => {
    setSaving(item.id);
    try {
      await updateFeatured(item.id, { is_visible: !item.is_visible });
      setFeatured((prev) => prev.map((f) => f.id === item.id ? { ...f, is_visible: !f.is_visible } : f));
    } catch (e) { setErr(e.message); }
    setSaving(null);
  };

  // 로컬 상태 즉시 반영 (onBlur에서 저장)
  const handleField = (id, key, val) =>
    setFeatured((prev) => prev.map((f) => f.id === id ? { ...f, [key]: val } : f));

  const handleBlur = async (item, key) => {
    setSaving(item.id);
    try { await updateFeatured(item.id, { [key]: item[key] }); }
    catch (e) { setErr(e.message); }
    setSaving(null);
  };

  const handleSelectChange = async (item, key, val) => {
    handleField(item.id, key, val);
    setSaving(item.id);
    try { await updateFeatured(item.id, { [key]: val }); }
    catch (e) { setErr(e.message); }
    setSaving(null);
  };

  const handleMove = async (idx, dir) => {
    const next = [...featured];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    const reindexed = next.map((f, i) => ({ ...f, order_index: i }));
    setFeatured(reindexed);
    try { await batchUpdateFeaturedOrder(reindexed.map(({ id, order_index }) => ({ id, order_index }))); }
    catch (e) { setErr(e.message); }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <AdminGuard>
      <div className="admin-page">
        <header className="admin-header">
          <button onClick={() => navigate('/admin/dashboard')} className="admin-btn-ghost">← 대시보드</button>
          <span className="admin-header-title">홈 피처드</span>
          <span style={{ fontSize: '0.75rem', color: 'var(--c-n400)' }}>변경사항 자동 저장</span>
        </header>

        <main className="admin-main">

          {/* ── 홈에 표시 중 ───────────────────────────────────────────── */}
          <h2 className="admin-section-title">홈에 표시 중 ({featured.length})</h2>
          {err && <p className="admin-error">{err}</p>}

          {loading ? (
            <p className="admin-loading">불러오는 중…</p>
          ) : featured.length === 0 ? (
            <p className="admin-loading">아직 추가된 프로젝트가 없습니다.</p>
          ) : (
            <div className="admin-feat-list">
              {featured.map((item, idx) => {
                const isSaving = saving === item.id;
                const isMainOnly = item.projects?.is_main_only;
                const btnLabel = item.button_label || 'View Project';
                const btnUrl   = item.button_url || '';

                return (
                  <div key={item.id} className={`admin-feat-card${isSaving ? ' admin-feat-card--saving' : ''}${!item.is_visible ? ' admin-feat-card--hidden' : ''}`}>

                    {/* ── 카드 상단: 썸네일 + 제목 + 순서 + 상태 ── */}
                    <div className="admin-feat-card-top">
                      <div className="admin-feat-order">
                        <button className="admin-btn-order" onClick={() => handleMove(idx, -1)} disabled={idx === 0}>↑</button>
                        <span className="admin-feat-num">{idx + 1}</span>
                        <button className="admin-btn-order" onClick={() => handleMove(idx, 1)} disabled={idx === featured.length - 1}>↓</button>
                      </div>

                      <div className="admin-feat-thumb">
                        {item.projects?.thumbnail_url
                          ? <img src={item.projects.thumbnail_url} alt="" />
                          : <div className="admin-feat-thumb-empty" />}
                      </div>

                      <div className="admin-feat-card-info">
                        <div className="admin-feat-title">
                          {item.projects?.title}
                          {isMainOnly && <span className="admin-badge admin-badge--main" style={{ marginLeft: '0.5rem' }}>메인전용</span>}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--c-n400)' }}>
                          {item.projects?.project_type}
                        </div>
                      </div>

                      <div className="admin-feat-card-actions">
                        <button
                          className={`admin-feat-toggle${item.is_visible ? ' admin-feat-toggle--on' : ''}`}
                          onClick={() => handleToggle(item)}
                          disabled={isSaving}
                          title={item.is_visible ? '노출 중 → 클릭하면 숨김' : '숨김 → 클릭하면 노출'}
                        >
                          {isSaving ? '…' : item.is_visible ? '노출' : '숨김'}
                        </button>
                        <button className="admin-btn-sm admin-btn-sm--danger" onClick={() => handleRemove(item.id)}>
                          제거
                        </button>
                      </div>
                    </div>

                    {/* ── 카드 하단: 버튼 설정 ── */}
                    <div className="admin-feat-btn-section">
                      <div className="admin-feat-btn-section-label">버튼 설정</div>

                      <div className="admin-feat-btn-fields">
                        <label className="admin-label" style={{ flex: '0 0 180px' }}>
                          버튼 텍스트
                          <input
                            className="admin-input admin-input--sm"
                            value={item.button_label || ''}
                            placeholder="View Project"
                            onChange={(e) => handleField(item.id, 'button_label', e.target.value)}
                            onBlur={() => handleBlur(item, 'button_label')}
                          />
                        </label>

                        <label className="admin-label" style={{ flex: 1 }}>
                          버튼 URL
                          <span className="admin-field-badge" style={{ marginLeft: '0.375rem' }}>
                            {btnUrl ? btnUrl : (isMainOnly ? '— 없음' : '비우면 상세 페이지로 이동')}
                          </span>
                          <input
                            className="admin-input admin-input--sm"
                            value={item.button_url || ''}
                            placeholder={isMainOnly ? 'https://...' : 'https://... (비우면 상세 페이지로)'}
                            onChange={(e) => handleField(item.id, 'button_url', e.target.value)}
                            onBlur={() => handleBlur(item, 'button_url')}
                          />
                        </label>

                        <label className="admin-label" style={{ flex: '0 0 110px' }}>
                          열기
                          <select
                            className="admin-select admin-select--sm"
                            value={item.button_target || '_self'}
                            onChange={(e) => handleSelectChange(item, 'button_target', e.target.value)}
                          >
                            <option value="_self">현재 탭</option>
                            <option value="_blank">새 탭</option>
                          </select>
                        </label>

                        {/* 미리보기 */}
                        <div className="admin-feat-btn-preview">
                          <span className="admin-feat-btn-preview-label">미리보기</span>
                          <span className="admin-feat-btn-preview-btn">
                            {btnLabel} →
                          </span>
                        </div>
                      </div>

                      {isMainOnly && !btnUrl && (
                        <p className="admin-field-hint" style={{ color: '#dc2626', marginTop: '0.5rem' }}>
                          ⚠ 메인 전용 게시물은 버튼 URL을 입력해야 클릭이 동작합니다.
                        </p>
                      )}
                    </div>

                  </div>
                );
              })}
            </div>
          )}

          {/* ── 추가 가능한 프로젝트 ──────────────────────────────────── */}
          {available.length > 0 && (
            <>
              <h2 className="admin-section-title" style={{ marginTop: '2.5rem' }}>추가 가능한 프로젝트</h2>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr><th>제목</th><th>타입</th><th>상태</th><th></th></tr>
                  </thead>
                  <tbody>
                    {available.map((p) => (
                      <tr key={p.id}>
                        <td style={{ fontWeight: 500 }}>
                          {p.title}
                          {p.is_main_only && (
                            <span className="admin-badge admin-badge--main" style={{ marginLeft: '0.375rem' }}>메인전용</span>
                          )}
                        </td>
                        <td><span className="admin-badge">{p.project_type}</span></td>
                        <td>
                          <span className={`admin-feat-toggle${p.is_visible ? ' admin-feat-toggle--on' : ''}`}
                            style={{ cursor: 'default' }}>
                            {p.is_visible ? '노출' : '숨김'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button className="admin-btn-sm" onClick={() => handleAdd(p.id)}>+ 홈에 추가</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

        </main>
      </div>
    </AdminGuard>
  );
}
